import { AppliedFilterBarOpt, FilterGroup, IFilterBar } from '../../../models/utilities/filter-bar';
import { ListColumn, SelectedSort } from '../../../models/utilities/list-header';
import { PageData } from '../../../models/utilities/paged-request';
import { ApiEndpoints } from '../../../utils/app-constants';
import { OrderShipmentStatus } from '../../../models/ordering/enums/order-shipment-status';
import { ShipmentMethod } from '../enums/_index';

/**
 * The orders a delivery can take, the way the API takes them: the ones that are open for shipping, so not on a
 * shipment yet.  Whether the order is in a production run does not matter, so there is no status to go with it.
 */
export const DELIVERABLE_SHIPMENT_STATUS = `${ OrderShipmentStatus.Open }`;

export class OrderSearchFilters implements IFilterBar {

    public paginationInfo   : PageData;
    public baseUrl          : string = ApiEndpoints.TRANSPORTATION_ORDERS_QUERY;
    public filters          : FilterGroup[] = [];
    public appliedFilters   : AppliedFilterBarOpt[] = [];
    public requiredFilters  : AppliedFilterBarOpt[] = [];
    public columns          : ListColumn[];

    public selectedSort     : SelectedSort;

    constructor(shipmentMethod : ShipmentMethod, addressId? : number, cutoff? : string, dealer? : string, source? : string, grouping? : number, serviceCenter? : string, status? : string, shipmentStatus? : string ) {

        this.columns = [
            new ListColumn('', '', false),
            new ListColumn('Order ID', 'orderId', false),
            new ListColumn('Description', 'description', false),
            new ListColumn('Job Name', 'jobName', false),
            new ListColumn('Cutoff Date', 'cutoff', false)
        ];

        this.paginationInfo = new PageData(10000);
        
        // The active orders, unless it is the shipment status that is asked for
        if (status !== undefined || !shipmentStatus) {
            const byStatus = this.buildStatus(status ?? '2');
            this.requiredFilters.push(byStatus);
        }

        if (shipmentStatus) {
            const byShipmentStatus = this.buildShipmentStatus(shipmentStatus);
            this.requiredFilters.push(byShipmentStatus);
        }

        // const byDivision = this.buildDivision();
        // this.requiredFilters.push(byDivision);

        const byMethod = this.buildMethod(shipmentMethod);
        this.requiredFilters.push(byMethod);

        if (addressId) {
            const byAddress     = this.buildShipmentId(addressId);
            this.requiredFilters.push(byAddress);
        }

        if (cutoff) {
            const byMaxCutoff   = this.buildMaxCutoff(cutoff);
            this.requiredFilters.push(byMaxCutoff);
        }

        if (dealer) {
            const byDealer = this.buildDealer(dealer);
            this.requiredFilters.push(byDealer);
        }   

        if (source) {
            const bySource = this.buildSource(source);
            this.requiredFilters.push(bySource);
        }  
        
        if (grouping !== null && grouping !== undefined) {
            const byGrouping = this.buildGrouping(grouping);
            this.requiredFilters.push(byGrouping);
        }  

        if (serviceCenter) {
            const bySC = this.buildServiceCenter(serviceCenter);
            this.requiredFilters.push(bySC);
        }  
        
    }

    // #region Group Builders

    private buildMethod(method : ShipmentMethod) {
        const requiredType      = new AppliedFilterBarOpt();
        requiredType.uuid       = '';
        requiredType.label      = 'shippingMethod';
        requiredType.qsName     = 'shippingMethod';
        requiredType.qsValue    = method.toString();

        return requiredType;
    }

    private buildDivision() {
        const requiredType      = new AppliedFilterBarOpt();
        requiredType.uuid       = '';
        requiredType.label      = 'division';
        requiredType.qsName     = 'division';
        requiredType.qsValue    = '0';

        return requiredType;
    }

    private buildStatus(status: string) {
        const requiredType      = new AppliedFilterBarOpt();
        requiredType.uuid       = '';
        requiredType.label      = 'status';
        requiredType.qsName     = 'status';
        requiredType.qsValue    = status;

        return requiredType;
    }

    private buildShipmentStatus(status: string) {
        const requiredType      = new AppliedFilterBarOpt();
        requiredType.uuid       = '';
        requiredType.label      = 'shipmentStatus';
        requiredType.qsName     = 'shipmentStatus';
        requiredType.qsValue    = status;

        return requiredType;
    }

    private buildShipmentId(id : number) {
        const requiredType      = new AppliedFilterBarOpt();
        requiredType.uuid       = '';
        requiredType.label      = 'addressId';
        requiredType.qsName     = 'shipToAddressId';
        requiredType.qsValue    = id.toString(); 

        return requiredType;
    }

    private buildMaxCutoff(cutoff : string) {
        const requiredType      = new AppliedFilterBarOpt();
        requiredType.uuid       = '';
        requiredType.label      = 'maxCutoff';
        requiredType.qsName     = 'maxCutoff';
        requiredType.qsValue    = cutoff;

        return requiredType;
    }

    private buildDealer(dealer : string) {
        const requiredType      = new AppliedFilterBarOpt();
        requiredType.uuid       = '';
        requiredType.label      = 'dealerId';
        requiredType.qsName     = 'dealerId';
        requiredType.qsValue    = dealer;

        return requiredType;
    }

    private buildSource(source : string) {
        const requiredSource      = new AppliedFilterBarOpt();
        requiredSource.uuid       = '';
        requiredSource.label      = 'source';
        requiredSource.qsName     = 'source';
        requiredSource.qsValue    = source;

        return requiredSource;
    }   

    private buildGrouping(groupId : number) {
        const requiredGroup      = new AppliedFilterBarOpt();
        requiredGroup.uuid       = '';
        requiredGroup.label      = 'orderGroupId';
        requiredGroup.qsName     = 'groupId';
        requiredGroup.qsValue    = groupId.toString();

        return requiredGroup;
    }   

    private buildServiceCenter(serviceCenter : string) {
        const requiredSC      = new AppliedFilterBarOpt();
        requiredSC.uuid       = '';
        requiredSC.label      = 'serviceCenterId';
        requiredSC.qsName     = 'serviceCenterId';
        requiredSC.qsValue    = serviceCenter;

        return requiredSC;
    }   

}
